import InfoIcon from "@mui/icons-material/Info";
import { Tooltip } from "@mui/material";
import {
  getGridDefaultColumnTypes,
  gridNumberComparator,
  useGridApiRef,
  useKeepGroupedColumnsHidden,
  type GetApplyFilterFn,
  type GridColDef,
  type GridComparatorFn,
  type GridFilterOperator,
  type GridRenderCellParams,
  type GridRowClassNameParams,
} from "@mui/x-data-grid-premium";
import React, { useContext, useEffect, useState } from "react";
import { PatientContext } from "../../../contexts/PatientContext.tsx";
import { ProjectContext } from "../../../contexts/ProjectContext.tsx";
import { useFetch } from "../../../services/hooks/useFetch.ts";
import { convertDate } from "../../../utils/dateTimeUtils.ts";
import { getDiffDaysMessage, projectsPrefix } from "../../../utils/main.ts";
import AccordionTable from "../../datagrid/AccordionTable.tsx";

const formNames = {
  koos: "KOOS",
  koosjr: "KOOSJR",
  oxford: "OXFORD",
  snot: "SNOT",
  pain_global: "PAIN_GLOBAL",
  pain_interference: "PAIN_INTERFERENCE",
  pain_screening: "PAIN_SCREENING",
  promis_social: "PROMIS_SOCIAL",
  promis_cognitive: "PROMIS_COGNITIVE",
  promis_emotional: "PROMIS_EMOTIONAL",
  promis_physical: "PROMIS_PHYSICAL",
  promis_upper_extremity: "PROMIS_UPPER_EXTREMITY",
};

const koosCategoryOrder: Record<string, number | undefined> = {
  symptoms: 1,
  pain: 2,
  adl: 3,
  "sport/rec": 4,
  qol: 5,
};

interface ReducedProm {
  type: string;
  diffDays?: number;
  promId?: string;
  question: string;
  answerText?: string;
  answerValue?: number;
  questionOrder?: number;
  questionSortOrder?: number;
  createdAt?: string;
}

// This component implements a lot of custom logic to render a DataGrid.
// This DataGrid uses a feature called "Row grouping" to show the row data in
// a hierarchy.  Answer data is shown as "individual row"s in the DataGrid.
// Form Type and Diff Days are shown as "group row"s in the DataGrid.
export default function PromDataExpandTable() {
  const defaultColumnTypes = getGridDefaultColumnTypes();
  const [answerRows, setAnswerRows] = useState<ReducedProm[]>([]);
  const { id: projectId, highlightDate } = useContext(ProjectContext)!;
  const { id, promCount } = useContext(PatientContext)!;

  const [shouldFetchProms, setShouldFetchProms] = useState(false);

  const {
    isLoading: isPromDataLoading,
    data: promData,
    fetch: fetchPromData,
  } = useFetch<Prom[]>();

  useEffect(() => {
    if (shouldFetchProms && !isPromDataLoading) {
      void fetchPromData(
        `${projectsPrefix}/${projectId}/patients/${id}/proms/`,
        {
          postResponseAction: (data) => {
            setShouldFetchProms(false);
            setAnswerRows(getDataGridRowsFromPromData(data));
          },
        },
      );
    }
  }, [projectId, id, fetchPromData, shouldFetchProms, isPromDataLoading]);

  // Convert the hierarchical data from the API response into a list of individual answer rows
  function getDataGridRowsFromPromData(promModelData: Prom[] | undefined) {
    if (!promModelData) {
      return [];
    }
    const answerRows = [];
    for (const promModel of promModelData) {
      for (const answerModel of promModel.answers ?? []) {
        const answerRow = {
          type: promModel.form?.name ?? promModel.formName ?? "Unknown Form",
          diffDays: promModel.diffDays,
          promId: promModel.id,
          question:
            answerModel.question?.questionText ??
            answerModel.questionText ??
            "Unknown Question",
          answerText: answerModel.answerText,
          answerValue: answerModel.answerValue,
          questionOrder: answerModel.question?.questionOrder,
          questionSortOrder: answerModel.question?.questionOrder,
          createdAt: promModel.createdAt,
        };

        // Most forms have question numbered 1->N, where N = total questions on the form
        // KOOS forms have questions numbered 1->C, where C = total questions in the question category
        // This means with 5 categories there are 5 questions with questionOrder=1
        // To make the sorting more natural on this form, sort by category and question order
        if (promModel.form?.scoreType === formNames.koos) {
          const categoryOffset =
            (koosCategoryOrder[answerModel.question?.category ?? ""] ?? 0) *
            100;
          answerRow.questionSortOrder =
            categoryOffset + (answerRow.questionOrder ?? 0);
        }

        answerRows.push(answerRow);
      }
    }

    // Default sorting for questions should be question order
    answerRows.sort((a, b) => {
      if (
        typeof a.questionSortOrder === "number" &&
        typeof b.questionSortOrder === "number"
      ) {
        return a.questionSortOrder - b.questionSortOrder;
      } else {
        if (
          typeof a.questionSortOrder === "number" ||
          typeof b.questionSortOrder === "number"
        ) {
          return (a.questionSortOrder ?? 0) - (b.questionSortOrder ?? 0);
        } else {
          return String(a.question).localeCompare(String(b.question));
        }
      }
    });

    return answerRows;
  }

  // Needed when using useKeepGroupedColumnsHidden utility in the initial state
  const apiRef = useGridApiRef();

  // useKeepGroupedColumnsHidden: Grouped columns are added to the whichever
  //  columns are defined in the component's `columns` prop.  Without this
  //  helper method, the column would appear twice in the DataGrid
  const initialState = useKeepGroupedColumnsHidden({
    initialState: {
      // Sets the columns to use for row grouping
      // promId is the value we use from the model for grouping, but the column is
      // displayed as diff days
      rowGrouping: {
        model: ["type", "promId"],
      },
    },
    apiRef,
  });

  const findProm = (promId: unknown) => promData?.find((p) => p.id === promId);

  const getPromIdColumnText = function (
    params: GridRenderCellParams<ReducedProm, string | undefined>,
  ) {
    const prom = findProm(params.value);
    if (!prom) {
      return "";
    }
    const promDiffString =
      prom.diffDays !== null ? getDiffDaysMessage(prom.diffDays) : "N.A.";
    const promTimeString =
      prom.promDate !== null
        ? new Date(prom.promDate).toLocaleTimeString([], {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          })
        : "";
    return (
      <span>
        {promDiffString}
        <br />
        {promTimeString}
      </span>
    );
  };

  const promIdFilterOperator = (
    operator: GridFilterOperator<ReducedProm, string | number | undefined>,
  ) => {
    const getApplyFilterFn: GetApplyFilterFn<
      ReducedProm,
      string | number | undefined
    > = (filterItem, column) => {
      const innerFilterFn = operator.getApplyFilterFn(filterItem, column);
      if (!innerFilterFn) {
        return innerFilterFn;
      }

      return (value, row, col, apiRef) => {
        const rowId = apiRef.current.getRowId(row);
        const rowNode = apiRef.current?.getRowNode(rowId);
        if (rowNode?.type === "group" && rowNode?.groupingField === "promId") {
          const prom = findProm(value);
          return innerFilterFn(prom?.diffDays, row, col, apiRef);
        }

        return false;
      };
    };

    return {
      ...operator,
      getApplyFilterFn,
    };
  };

  const promIdSortComparator: GridComparatorFn<string> = function (
    _v1,
    _v2,
    param1,
    param2,
  ) {
    if (
      param1.rowNode.type === "group" &&
      param2.rowNode.type === "group" &&
      param1.rowNode.groupingField === param2.rowNode.groupingField &&
      param1.rowNode.groupingField === "promId"
    ) {
      const promRow1 = findProm(param1.rowNode.groupingKey);
      const diffDays1 = promRow1?.diffDays;

      const promRow2 = findProm(param2.rowNode.groupingKey);
      const diffDays2 = promRow2?.diffDays;

      return gridNumberComparator(diffDays1, diffDays2, param1, param2);
    }

    return 0;
  };

  const getYearColumnText = function (_value: undefined, row: ReducedProm) {
    const rowId = apiRef.current?.getRowId(row);
    if (!rowId) {
      return null;
    }

    const rowNode = apiRef.current?.getRowNode(rowId);
    if (rowNode?.type === "group" && rowNode?.groupingField === "promId") {
      const promRow = findProm(rowNode.groupingKey);

      if (!promRow) {
        return null;
      }

      const date = new Date(promRow.promDate);
      return date.getFullYear();
    }
    return null;
  };

  const yearSortComparator: GridComparatorFn<number> = function (
    _v1,
    _v2,
    param1,
    param2,
  ) {
    if (
      param1.rowNode.type === "group" &&
      param2.rowNode.type === "group" &&
      param1.rowNode.groupingField === param2.rowNode.groupingField &&
      param1.rowNode.groupingField === "promId"
    ) {
      const promRow1 = findProm(param1.rowNode.groupingKey);
      const date1 = new Date(promRow1?.promDate ?? NaN);

      const promRow2 = findProm(param2.rowNode.groupingKey);
      const date2 = new Date(promRow2?.promDate ?? NaN);

      return gridNumberComparator(
        date1.getFullYear(),
        date2.getFullYear(),
        param1,
        param2,
      );
    }

    return 0;
  };

  const getQuestionColumnText = function (
    params: GridRenderCellParams<ReducedProm, string>,
  ) {
    // Default case for an individual row is to show the question value
    // Wrap it in a span so we can add a min-height
    if (params.value) {
      return <span className={"prom-answer-text"}>{params.value}</span>;
    }

    // For a PROM grouped row, display the score summary
    if (
      "groupingField" in params.rowNode &&
      params.rowNode.groupingField === "promId"
    ) {
      const promId = params.rowNode.groupingKey;
      const promRow = findProm(promId);
      switch (promRow?.form?.scoreType) {
        case formNames.koos:
          return renderKoosSummary(promRow);
        case formNames.koosjr:
          return renderKoosjrSummary(promRow);
        case formNames.oxford:
          return renderOxfordSummary(promRow);
        case formNames.snot:
          return renderSnotForm(promRow);
        case formNames.pain_global:
          return renderPainGlobalForm(promRow);
        case formNames.pain_interference:
          return renderPainInterferenceForm(promRow);
        case formNames.pain_screening:
          return renderPainScreeningForm(promRow);
        case formNames.promis_cognitive: {
          const PROMIS_COGNITIVE_QUESTION_COUNT = 32;
          return renderPromisActivityForm(
            promRow,
            PROMIS_COGNITIVE_QUESTION_COUNT,
          );
        }
        case formNames.promis_emotional: {
          const PROMIS_EMOTIONAL_QUESTION_COUNT = 16;
          return renderPromisActivityForm(
            promRow,
            PROMIS_EMOTIONAL_QUESTION_COUNT,
          );
        }
        case formNames.promis_physical: {
          const PROMIS_PHYSICAL_QUESTION_COUNT = 165;
          return renderPromisActivityForm(
            promRow,
            PROMIS_PHYSICAL_QUESTION_COUNT,
          );
        }
        case formNames.promis_social: {
          const PROMIS_SOCIAL_QUESTION_COUNT = 35;
          return renderPromisActivityForm(
            promRow,
            PROMIS_SOCIAL_QUESTION_COUNT,
          );
        }
        case formNames.promis_upper_extremity: {
          const PROMIS_UPPER_EXTREMITY_QUESTION_COUNT = 46;
          return renderPromisActivityForm(
            promRow,
            PROMIS_UPPER_EXTREMITY_QUESTION_COUNT,
          );
        }
        default:
          return null;
      }
    }

    // Don't show anything for the Type group row
    return null;
  };

  const getRowClassName = function (params: GridRowClassNameParams) {
    const rowId = apiRef.current?.getRowId(params.row);
    if (!rowId) {
      return "";
    }

    const rowNode = apiRef.current?.getRowNode(rowId);
    if (
      !rowNode ||
      (rowNode?.type === "group" && rowNode?.groupingField === "promId")
    ) {
      const promId = rowNode ? rowNode.groupingKey : params.row.promId;
      const promRow = findProm(promId);

      const rowClassName =
        highlightDate &&
        convertDate.dateIsAfterHighlight(highlightDate, promRow?.createdAt) &&
        "MuiDataGrid-row--highlighted";

      return rowClassName ? String(rowClassName) : "";
    }
    return "";
  };

  // Each of the GetXColumnText/XSortComparator methods used below are called once per
  // row when for a given column in the DataGrid.  For a group row, we need custom logic
  // to override the column text (or sort value). In order to determine if we're
  // on a group row or an individual row, check the vale of "params.rowNode.groupingField".
  // For a group row, this will equal the name of the column that is being grouped.
  const dataGridColumns: GridColDef[] = [
    {
      field: "type",
      headerName: "Type",
      width: 100,
      hideable: false,
      sortable: false,
      filterable: false,
    },
    {
      field: "promId",
      headerName: "Pre/Post Index",
      width: 150,
      type: "string",
      renderCell: getPromIdColumnText,
      sortComparator: promIdSortComparator,
      filterOperators: defaultColumnTypes["string"]?.filterOperators?.map(
        (operator) => promIdFilterOperator(operator),
      ),
      hideable: false,
      sortable: false,
      filterable: false,
    },
    {
      field: "year",
      headerName: "Year",
      width: 100,
      valueFormatter: getYearColumnText,
      sortComparator: yearSortComparator,
    },
    {
      field: "question",
      headerName: "Question Text",
      width: 250,
      renderCell: getQuestionColumnText,
      // For a PROM row, the question column spans the remainder of the table's columns
      // For all other rows, use single column span
      colSpan: (value) => {
        return !value ? 3 : 1;
      },
    },
    {
      field: "answerText",
      headerName: "Answer Text",
      width: 100,
    },
    {
      field: "answerValue",
      headerName: "Answer Value",
      width: 100,
    },
  ];

  // The accordion component setup was taken from the StructuredDataExpandTable component
  // We probably should move the accordion part into a wrapper component
  // if we need another custom component inside the accordion
  return (
    <AccordionTable<ReducedProm>
      loading={shouldFetchProms}
      title={"Patient Reported Outcomes"}
      totalCount={promCount?.total ?? 0}
      highlightCount={promCount?.highlight}
      tableHeight={600}
      triggerFetch={() => setShouldFetchProms(true)}
      apiRef={apiRef}
      initialState={initialState}
      rows={answerRows}
      getRowHeight={() => {
        return "auto";
      }}
      getRowClassName={getRowClassName}
      // Lots of custom logic in here for rendering row cells
      columns={dataGridColumns}
      // By default, grouped columns are all combined into the first column
      // This will make each of the grouped columns into an individual columns
      rowGroupingColumnMode="multiple"
      rowGroupingModel={["type", "promId"]}
      disableRowGrouping={false}
      // Hide total row count on the grouped rows
      groupingColDef={{
        hideDescendantCount: true,
        hideable: false,
        filterable: true,
        sortable: true,
      }}
      // Hide the footer row count
      // The count will display as the number of top level groups (ie: form types)
      hideFooterRowCount
      pagination={false}
      // I tried to implement filtering but kept getting errors about rows not existing
      // I'm not really sure what's going on, but supporting filtering will need some custom
      // work.
      disableColumnFilter
      tableId="prom-data-table"
      sx={{
        "& .MuiDataGrid-cell": {
          alignContent: "center",
        },
      }}
    />
  );
}

function renderPromisActivityForm(
  promRow: Prom,
  expectedQuestionCount: number,
) {
  if (promRow.score) {
    return (
      <div className={"prom-group-cell"}>
        <div className={"prom-group-cell-row"}>
          <div className={"prom-group-title"}>
            <div>
              Survey
              <br />
              Scores
            </div>
          </div>
          <div className={"prom-group-label"}>
            <div className={"prom-questions-pill"}>
              {getAnswerCountForProm(promRow)}/{expectedQuestionCount}
            </div>
            <div>
              Questions
              <br />
              Answered
            </div>
          </div>
          <div className={"prom-group-label"}>
            <div className={"prom-raw-score-pill"}>
              {promRow.score}/{expectedQuestionCount * 5}
            </div>
            <div>
              Total
              <br />
              Score
            </div>
          </div>
        </div>
        <div className={"prom-group-cell-row"}>
          <div className={"prom-group-title"}>
            {renderSubscoreToolTip(promRow.form?.scoreType)}
            <div>
              Survey
              <br />
              Subscores
            </div>
          </div>
          <div className={"prom-group-label"}>N/A</div>
        </div>
      </div>
    );
  } else {
    return renderUnscoredSummary(
      promRow.form?.scoreType,
      getAnswerCountForProm(promRow),
      expectedQuestionCount,
    );
  }
}

function renderPainScreeningForm(promRow: Prom) {
  if (promRow.score) {
    return (
      <div className={"prom-group-cell"}>
        <div className={"prom-group-cell-row"}>
          <div className={"prom-group-title"}>
            <div>
              Survey
              <br />
              Scores
            </div>
          </div>
          <div className={"prom-group-label"}>
            <div className={"prom-questions-pill"}>
              {getAnswerCountForProm(promRow)}/3
            </div>
            <div>
              Questions
              <br />
              Answered
            </div>
          </div>
          <div className={"prom-group-label"}>
            <div className={"prom-raw-score-pill"}>{promRow.score ?? 0}/10</div>
            <div>
              Total
              <br />
              Score
            </div>
          </div>
        </div>
        <div className={"prom-group-cell-row"}>
          <div className={"prom-group-title"}>
            {renderSubscoreToolTip(promRow.form?.scoreType)}
            <div>
              Survey
              <br />
              Subscores
            </div>
          </div>
          <div className={"prom-group-label"}>N/A</div>
        </div>
      </div>
    );
  } else {
    return renderUnscoredSummary(
      promRow.form?.scoreType,
      getAnswerCountForProm(promRow),
      7,
    );
  }
}

function renderPainInterferenceForm(promRow: Prom) {
  if (promRow.score) {
    return (
      <div className={"prom-group-cell"}>
        <div className={"prom-group-cell-row"}>
          <div className={"prom-group-title"}>
            <div>
              Survey
              <br />
              Scores
            </div>
          </div>
          <div className={"prom-group-label"}>
            <div className={"prom-questions-pill"}>
              {getAnswerCountForProm(promRow)}/40
            </div>
            <div>
              Questions
              <br />
              Answered
            </div>
          </div>
          <div className={"prom-group-label"}>
            <div className={"prom-raw-score-pill"}>
              {promRow.scoreDetail?.["Raw summed score"] ?? 0}/200
            </div>
            <div>
              Total
              <br />
              Score
            </div>
          </div>
        </div>
        <div className={"prom-group-cell-row"}>
          <div className={"prom-group-title"}>
            {renderSubscoreToolTip(promRow.form?.scoreType)}
            <div>
              Survey
              <br />
              Subscores
            </div>
          </div>
          <div className={"prom-group-label"}>N/A</div>
        </div>
      </div>
    );
  } else {
    return renderUnscoredSummary(
      promRow.form?.scoreType,
      getAnswerCountForProm(promRow),
      40,
    );
  }
}

function renderPainGlobalForm(promRow: Prom) {
  if (promRow.score) {
    return (
      <div className={"prom-group-cell"}>
        <div className={"prom-group-cell-row"}>
          <div className={"prom-group-title"}>
            <div>
              Survey
              <br />
              Scores
            </div>
          </div>
          <div className={"prom-group-label"}>
            <div className={"prom-questions-pill"}>
              {getAnswerCountForProm(promRow)}/7
            </div>
            <div>
              Questions
              <br />
              Answered
            </div>
          </div>
          <div className={"prom-group-label"}>
            <div className={"prom-raw-score-pill"}>
              {promRow.scoreDetail?.["Raw summed score"] ?? 0}/610
            </div>
            <div>
              Total
              <br />
              Score
            </div>
          </div>
        </div>
        <div className={"prom-group-cell-row"}>
          <div className={"prom-group-title"}>
            {renderSubscoreToolTip(promRow.form?.scoreType)}
            <div>
              Survey
              <br />
              Subscores
            </div>
          </div>
          <div className={"prom-group-label"}>N/A</div>
        </div>
      </div>
    );
  } else {
    return renderUnscoredSummary(
      promRow.form?.scoreType,
      getAnswerCountForProm(promRow),
      7,
    );
  }
}

function renderSnotForm(promRow: Prom) {
  if (promRow.score) {
    return (
      <div className={"prom-group-cell"}>
        <div className={"prom-group-cell-row"}>
          <div className={"prom-group-title"}>
            <div>
              Survey
              <br />
              Scores
            </div>
          </div>
          <div className={"prom-group-label"}>
            <div className={"prom-questions-pill"}>
              {getAnswerCountForProm(promRow)}/22
            </div>
            <div>
              Questions
              <br />
              Answered
            </div>
          </div>
          <div className={"prom-group-label"}>
            <div className={"prom-raw-score-pill"}>
              {(promRow.scoreDetail?.["Raw summed score"] ?? 0) ||
                (promRow.scoreDetail?.["Total snot score"] ?? 0)}
              /110
            </div>
            <div>
              Total SNOT
              <br />
              Score
            </div>
          </div>
        </div>
        <div className={"prom-group-cell-row"}>
          <div className={"prom-group-title"}>
            {renderSubscoreToolTip(promRow.form?.scoreType)}
            <div>
              Survey
              <br />
              Subscores
            </div>
          </div>
          <div className={"prom-group-label"}>N/A</div>
        </div>
      </div>
    );
  } else {
    return renderUnscoredSummary(
      promRow.form?.scoreType,
      getAnswerCountForProm(promRow),
      22,
    );
  }
}

function renderKoosjrSummary(promRow: Prom) {
  if (promRow.score) {
    return (
      <div className={"prom-group-cell"}>
        <div className={"prom-group-cell-row"}>
          <div className={"prom-group-title"}>
            <div>
              Survey
              <br />
              Scores
            </div>
          </div>
          <div className={"prom-group-label"}>
            <div className={"prom-questions-pill"}>
              {getAnswerCountForProm(promRow)}/7
            </div>
            <div>
              Questions
              <br />
              Answered
            </div>
          </div>
          <div className={"prom-group-label"}>
            <div className={"prom-raw-score-pill"}>
              {promRow.scoreDetail?.["Raw summed score"] ?? 0}/28
            </div>
            <div>
              Raw
              <br />
              Score
            </div>
          </div>
          <div className={"prom-group-label"}>
            <div className={"prom-score-pill"}>{promRow.score}/100</div>
            <div>
              KOOS JR
              <br />
              Score
            </div>
          </div>
        </div>
        <div className={"prom-group-cell-row"}>
          <div className={"prom-group-title"}>
            {renderSubscoreToolTip(promRow.form?.scoreType)}
            <div>
              Survey
              <br />
              Subscores
            </div>
          </div>
          <div className={"prom-group-label"}>N/A</div>
        </div>
      </div>
    );
  } else {
    return renderUnscoredSummary(
      promRow.form?.scoreType,
      getAnswerCountForProm(promRow),
      7,
    );
  }
}

function renderOxfordSummary(promRow: Prom) {
  if (promRow.score) {
    return (
      <div className={"prom-group-cell"}>
        <div className={"prom-group-cell-row"}>
          <div className={"prom-group-title"}>
            <div>
              Survey
              <br />
              Scores
            </div>
          </div>
          <div className={"prom-group-label"}>
            <div className={"prom-questions-pill"}>
              {getAnswerCountForProm(promRow)}/12
            </div>
            <div>
              Questions
              <br />
              Answered
            </div>
          </div>
          <div className={"prom-group-label"}>
            <div className={"prom-raw-score-pill"}>{promRow.score}/48</div>
            <div>
              Raw
              <br />
              Score
            </div>
          </div>
          <div className={"prom-group-label"}>
            <div className={"prom-score-pill"}>{promRow.score}</div>
            <div>
              Oxford
              <br />
              Knee
              <br />
              Score
            </div>
          </div>
        </div>
        <div className={"prom-group-cell-row"}>
          <div className={"prom-group-title"}>
            {renderSubscoreToolTip(promRow.form?.scoreType)}
            <div>
              Survey
              <br />
              Subscores
            </div>
          </div>
          <div className={"prom-group-label"}>
            <div className={"prom-subscore-pill"}>
              {promRow.scoreDetail?.pain}
            </div>
            <div>Pain</div>
          </div>
          <div className={"prom-group-label"}>
            <div className={"prom-subscore-pill"}>
              {promRow.scoreDetail?.function}
            </div>
            <div>Function</div>
          </div>
        </div>
      </div>
    );
  } else {
    return renderUnscoredSummary(
      promRow.form?.scoreType,
      getAnswerCountForProm(promRow),
      12,
    );
  }
}

function renderKoosSummary(promRow: Prom) {
  if (promRow.score) {
    return (
      <div className={"prom-group-cell"}>
        <div className={"prom-group-cell-row"}>
          <div className={"prom-group-title"}>
            <div>
              Survey
              <br />
              Scores
            </div>
          </div>
          <div className={"prom-group-label"}>
            <div className={"prom-questions-pill"}>
              {getAnswerCountForProm(promRow)}/42
            </div>
            <div>
              Questions
              <br />
              Answered
            </div>
          </div>
          <div className={"prom-group-label"}>
            <div className={"prom-raw-score-pill"}>
              {promRow.scoreDetail?.["Raw summed score"] ?? 0}/168
            </div>
            <div>
              Raw
              <br />
              Score
            </div>
          </div>
          <div className={"prom-group-label"}>
            <div className={"prom-score-pill"}>{promRow.score ?? 0}%</div>
            <div>
              KOOS
              <br />
              Score
            </div>
          </div>
        </div>
        <div className={"prom-group-cell-row koos-subscore-row"}>
          <div className={"prom-group-title"}>
            {renderSubscoreToolTip(promRow.form?.scoreType)}
            <div>
              Survey
              <br />
              Subscores
            </div>
          </div>
          <div className={"prom-group-label"}>
            <div className={"prom-subscore-pill"}>
              {promRow.scoreDetail?.symptoms ?? 0}%
            </div>
            <div className={"prom-subscore-label"}>
              Sympt
              <br />
              oms
            </div>
          </div>
          <div className={"prom-group-label"}>
            <div className={"prom-subscore-pill"}>
              {promRow.scoreDetail?.pain ?? 0}%
            </div>
            <div className={"prom-subscore-label"}>Pain</div>
          </div>
          <div className={"prom-group-label"}>
            <div className={"prom-subscore-pill"}>
              {promRow.scoreDetail?.adl ?? 0}%
            </div>
            <div className={"prom-subscore-label"}>Daily Living</div>
          </div>
          <div className={"prom-group-label"}>
            <div className={"prom-subscore-pill"}>
              {promRow.scoreDetail?.["sport/rec"] ?? 0}%
            </div>
            <div className={"prom-subscore-label"}>Sport / Rec.</div>
          </div>
          <div className={"prom-group-label"}>
            <div className={"prom-subscore-pill"}>
              {promRow.scoreDetail?.qol ?? 0}%
            </div>
            <div className={"prom-subscore-label"}>Quality of Life</div>
          </div>
        </div>
      </div>
    );
  } else {
    return renderUnscoredSummary(
      promRow.form?.scoreType,
      getAnswerCountForProm(promRow),
      42,
    );
  }
}

function renderUnscoredSummary(
  formType: string | undefined,
  answerCount: number,
  questionCount: number,
) {
  return (
    <div className={"prom-group-cell"}>
      <div className={"prom-group-cell-row"}>
        <div className={"prom-group-title"}>
          <div>
            Survey
            <br />
            Scores
          </div>
        </div>
        <div className={"prom-group-label"}>
          <div className={"prom-questions-pill"}>
            {answerCount}/{questionCount}
          </div>
          <div>
            Questions
            <br />
            Answered
          </div>
        </div>
        <div className={"prom-group-label"}>
          Scores not calculated because one or more answers are missing.
        </div>
      </div>
      <div className={"prom-group-cell-row"}>
        <div className={"prom-group-title"}>
          {renderSubscoreToolTip(formType)}
          <div>
            Survey
            <br />
            Subscores
          </div>
        </div>
        <div className={"prom-group-label"}>N/A</div>
      </div>
    </div>
  );
}

function renderSubscoreToolTip(type: string | undefined) {
  const subscoreToolTips = {
    [formNames.koos]:
      "The KOOS Survey has five subscales that are scored separately.",
    [formNames.koosjr]:
      "Unlike some other surveys, the KOOS JR does not include subscales.",
    [formNames.oxford]:
      "The Oxford Knee Survey has two subscales that are scored separately.",
    [formNames.snot]: "The SNOT survey does not include subscales.",
    [formNames.pain_global]:
      "The Global Pain survey does not include subscales.",
    [formNames.pain_interference]:
      "The Pain Interference survey does not include subscales.",
    [formNames.pain_screening]:
      "The Pain Screening survey does not include subscales.",
    [formNames.promis_cognitive]:
      "The PROMIS Cognitive survey does not include subscales.",
    [formNames.promis_emotional]:
      "The PROMIS Emotional survey does not include subscales.",
    [formNames.promis_physical]:
      "The PROMIS Physical survey does not include subscales.",
    [formNames.promis_social]:
      "The PROMIS Social survey does not include subscales.",
    [formNames.promis_upper_extremity]:
      "The PROMIS Upper Extremity survey does not include subscales.",
  };
  return (
    <Tooltip
      title={type ? (subscoreToolTips[type] ?? "") : ""}
      placement="top-end"
    >
      <InfoIcon
        sx={{ position: "absolute", right: "105%", top: "25%" }}
        color="disabled"
      />
    </Tooltip>
  );
}

function getAnswerCountForProm(promRow: Prom) {
  return (promRow.answers ?? []).filter(
    (answer) =>
      // Only count answers that have a valid scorable question
      Number.isInteger(answer.question?.questionOrder) &&
      // Only count answers that have "ANSWER PROVIDED FLAG" = true
      answer.provided,
  ).length;
}
